import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  MessageSquare, Mail, Heart, Calendar, Eye, Trash2, Download, Check,
  Users, Waves, HeartHandshake, Filter, Newspaper, HandHeart
} from 'lucide-react'
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc, where } from 'firebase/firestore'
import { db } from '../../services/firebase'
import { formatDate, formatRelativeTime } from '../../utils/helpers'
import { Button, Modal, Card, Badge } from '../../components/common'

const tabs = [
  { id: 'all', label: 'All Submissions', icon: MessageSquare, formType: null },
  { id: 'connect-card', label: 'Connect Card', icon: HandHeart, formType: 'connect-card' },
  { id: 'newsletter', label: 'Newsletter', icon: Newspaper, formType: 'newsletter', collection: 'newsletter' },
  { id: 'join-group', label: 'Join A Group', icon: Users, formType: 'join-group' },
  { id: 'baptism', label: 'Baptism Requests', icon: Waves, formType: 'baptism' },
  { id: 'volunteer', label: 'Volunteer', icon: HeartHandshake, formType: 'volunteer' },
  { id: 'contact', label: 'Contact', icon: Mail, formType: 'contact' },
  { id: 'prayer', label: 'Prayer Requests', icon: Heart, formType: 'prayer' },
  { id: 'visit', label: 'Visit Registrations', icon: Calendar, formType: 'visit' },
]

const FormSubmissions = () => {
  const [activeTab, setActiveTab] = useState('all')
  const [selectedSubmission, setSelectedSubmission] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [submissions, setSubmissions] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!db) {
      setLoading(false)
      return
    }

    const currentTab = tabs.find(t => t.id === activeTab)
    let q

    // Handle newsletter collection separately
    if (currentTab?.collection === 'newsletter') {
      q = query(
        collection(db, 'newsletter'),
        orderBy('subscribedAt', 'desc')
      )
    } else if (currentTab?.formType) {
      q = query(
        collection(db, 'formSubmissions'),
        where('formType', '==', currentTab.formType),
        orderBy('createdAt', 'desc')
      )
    } else {
      q = query(
        collection(db, 'formSubmissions'),
        orderBy('createdAt', 'desc')
      )
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setSubmissions(data)
      setLoading(false)
    }, (error) => {
      console.error('Error fetching submissions:', error)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [activeTab])

  const markAsRead = async (submission) => {
    if (!db) return
    try {
      await updateDoc(doc(db, 'formSubmissions', submission.id), { status: 'read' })
    } catch (error) {
      console.error('Error marking as read:', error)
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm || !db) return
    try {
      const currentTab = tabs.find(t => t.id === activeTab)
      const collectionName = currentTab?.collection === 'newsletter' ? 'newsletter' : 'formSubmissions'
      await deleteDoc(doc(db, collectionName, deleteConfirm.id))
      setDeleteConfirm(null)
      setSelectedSubmission(null)
    } catch (error) {
      console.error('Error deleting:', error)
    }
  }

  const exportToCsv = () => {
    if (!submissions?.length) return

    let headers, rows

    if (isNewsletterTab) {
      headers = ['Email', 'Name', 'Status', 'Subscribed Date']
      rows = submissions.map(s => [
        s.email || '',
        s.name || '',
        s.active !== false ? 'Active' : 'Inactive',
        s.subscribedAt || ''
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    } else {
      headers = ['Name', 'Email', 'Phone', 'Form Type', 'Status', 'Date', 'Message']
      rows = submissions.map(s => [
        `${s.firstName || ''} ${s.lastName || s.name || ''}`.trim(),
        s.email || '',
        s.phone || '',
        s.formType || '',
        s.status || '',
        s.createdAt?.toDate?.() ? formatDate(s.createdAt.toDate()) : '',
        s.message || s.prayerRequest || ''
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    }

    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${activeTab}-${isNewsletterTab ? 'subscribers' : 'submissions'}.csv`
    a.click()
  }

  const unreadCount = submissions?.filter(s => s.status !== 'read').length || 0

  const isNewsletterTab = activeTab === 'newsletter'

  const getFormTypeBadge = (formType) => {
    const colors = {
      'join-group': 'bg-primary-100 text-primary-700',
      'baptism': 'bg-secondary-100 text-secondary-700',
      'volunteer': 'bg-accent-100 text-accent-700',
      'contact': 'bg-blue-100 text-blue-700',
      'prayer': 'bg-pink-100 text-pink-700',
      'visit': 'bg-green-100 text-green-700',
      'newsletter': 'bg-indigo-100 text-indigo-700',
      'connect-card': 'bg-amber-100 text-amber-700',
    }
    const labels = {
      'join-group': 'Join Group',
      'baptism': 'Baptism',
      'volunteer': 'Volunteer',
      'contact': 'Contact',
      'prayer': 'Prayer',
      'visit': 'Visit',
      'newsletter': 'Newsletter',
      'connect-card': 'Connect Card',
    }
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[formType] || 'bg-gray-100 text-gray-700'}`}>
        {labels[formType] || formType}
      </span>
    )
  }

  const getName = (submission) => {
    if (submission.firstName && submission.lastName) {
      return `${submission.firstName} ${submission.lastName}`
    }
    return submission.name || 'Unknown'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Form Submissions</h1>
          <p className="text-gray-500">View and manage all form submissions</p>
        </div>
        <Button variant="outline" icon={Download} onClick={exportToCsv}>
          Export CSV
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-100 text-primary-700 font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
            {tab.id === activeTab && unreadCount > 0 && (
              <span className="bg-accent-500 text-white text-xs px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Submissions Table */}
      <Card animate={false} className="overflow-hidden p-0">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : submissions?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {!isNewsletterTab && (
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
                  )}
                  {!isNewsletterTab && (
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Type</th>
                  )}
                  {isNewsletterTab && (
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
                  )}
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">{isNewsletterTab ? 'Name' : 'Name'}</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Email</th>
                  {!isNewsletterTab && (
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Details</th>
                  )}
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">{isNewsletterTab ? 'Subscribed' : 'Submitted'}</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {submissions.map((submission) => (
                  <tr
                    key={submission.id}
                    className={`hover:bg-gray-50 cursor-pointer ${!isNewsletterTab && submission.status !== 'read' ? 'bg-primary-50/50' : ''}`}
                    onClick={() => {
                      setSelectedSubmission(submission)
                      if (!isNewsletterTab && submission.status !== 'read') markAsRead(submission)
                    }}
                  >
                    {!isNewsletterTab && (
                      <td className="px-6 py-4">
                        {submission.status === 'read' ? (
                          <Badge variant="gray" size="sm">Read</Badge>
                        ) : (
                          <Badge variant="accent" size="sm" dot>New</Badge>
                        )}
                      </td>
                    )}
                    {!isNewsletterTab && (
                      <td className="px-6 py-4">
                        {getFormTypeBadge(submission.formType)}
                      </td>
                    )}
                    {isNewsletterTab && (
                      <td className="px-6 py-4">
                        {submission.active !== false ? (
                          <Badge variant="emerald" size="sm">Active</Badge>
                        ) : (
                          <Badge variant="gray" size="sm">Inactive</Badge>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 font-medium text-dark-100">
                      {isNewsletterTab ? (submission.name || '-') : getName(submission)}
                    </td>
                    <td className="px-6 py-4 text-gray-600">{submission.email}</td>
                    {!isNewsletterTab && (
                      <td className="px-6 py-4 text-gray-600 truncate max-w-xs">
                        {submission.formType === 'join-group' && submission.preferredDay && (
                          <span>Prefers: {submission.preferredDay}</span>
                        )}
                        {submission.formType === 'baptism' && submission.salvationDate && (
                          <span>Saved: {submission.salvationDate}</span>
                        )}
                        {submission.formType === 'volunteer' && submission.ministry && (
                          <span>Ministry: {submission.ministry}</span>
                        )}
                        {submission.formType === 'contact' && submission.subject && (
                          <span>{submission.subject}</span>
                        )}
                        {submission.formType === 'prayer' && submission.prayerRequest && (
                          <span>{submission.prayerRequest?.slice(0, 40)}...</span>
                        )}
                        {submission.formType === 'visit' && submission.visitDate && (
                          <span>Visit: {formatDate(submission.visitDate)}</span>
                        )}
                        {submission.formType === 'connect-card' && (
                          <span>
                            {submission.visitorType === 'first_time' ? 'First-time' : 'Returning'}
                            {submission.serviceAttended ? ` · ${submission.serviceAttended.replace('_', ' ')}` : ''}
                          </span>
                        )}
                        {!submission.formType && submission.message && (
                          <span>{submission.message?.slice(0, 40)}...</span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 text-gray-500 text-sm">
                      {isNewsletterTab
                        ? formatRelativeTime(submission.subscribedAt)
                        : formatRelativeTime(submission.createdAt?.toDate?.() || submission.createdAt)
                      }
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedSubmission(submission)
                            if (!isNewsletterTab && submission.status !== 'read') markAsRead(submission)
                          }}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setDeleteConfirm(submission)
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No submissions yet</p>
          </div>
        )}
      </Card>

      {/* View Submission Modal */}
      <Modal
        isOpen={!!selectedSubmission}
        onClose={() => setSelectedSubmission(null)}
        title={isNewsletterTab ? "Subscriber Details" : "Submission Details"}
        size="lg"
      >
        {selectedSubmission && (
          <div className="space-y-6">
            {/* Form Type Badge */}
            <div className="flex items-center gap-3">
              {isNewsletterTab ? (
                <>
                  {getFormTypeBadge('newsletter')}
                  {selectedSubmission.active !== false ? (
                    <Badge variant="emerald" size="sm">Active</Badge>
                  ) : (
                    <Badge variant="gray" size="sm">Inactive</Badge>
                  )}
                </>
              ) : (
                <>
                  {getFormTypeBadge(selectedSubmission.formType)}
                  {selectedSubmission.status !== 'read' && (
                    <Badge variant="accent" size="sm">New</Badge>
                  )}
                </>
              )}
            </div>

            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-4">
              {!isNewsletterTab && (
                <div>
                  <label className="text-sm text-gray-500">Name</label>
                  <p className="font-medium text-dark-100">{getName(selectedSubmission)}</p>
                </div>
              )}
              {isNewsletterTab && selectedSubmission.name && (
                <div>
                  <label className="text-sm text-gray-500">Name</label>
                  <p className="font-medium text-dark-100">{selectedSubmission.name}</p>
                </div>
              )}
              <div>
                <label className="text-sm text-gray-500">Email</label>
                <p className="font-medium text-dark-100">
                  <a href={`mailto:${selectedSubmission.email}`} className="text-primary-600 hover:underline">
                    {selectedSubmission.email}
                  </a>
                </p>
              </div>
              {selectedSubmission.phone && (
                <div>
                  <label className="text-sm text-gray-500">Phone</label>
                  <p className="font-medium text-dark-100">
                    <a href={`tel:${selectedSubmission.phone}`} className="text-primary-600 hover:underline">
                      {selectedSubmission.phone}
                    </a>
                  </p>
                </div>
              )}
              <div>
                <label className="text-sm text-gray-500">{isNewsletterTab ? 'Subscribed' : 'Submitted'}</label>
                <p className="font-medium text-dark-100">
                  {isNewsletterTab
                    ? formatDate(selectedSubmission.subscribedAt, 'PPpp')
                    : formatDate(selectedSubmission.createdAt?.toDate?.() || selectedSubmission.createdAt, 'PPpp')
                  }
                </p>
              </div>
            </div>

            {/* Join Group Fields */}
            {selectedSubmission.formType === 'join-group' && (
              <div className="bg-primary-50 p-4 rounded-xl">
                <h4 className="font-medium text-primary-700 mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Small Group Interest
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {selectedSubmission.preferredDay && (
                    <div>
                      <label className="text-sm text-gray-500">Preferred Day</label>
                      <p className="font-medium text-dark-100 capitalize">{selectedSubmission.preferredDay}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Baptism Fields */}
            {selectedSubmission.formType === 'baptism' && (
              <div className="bg-secondary-50 p-4 rounded-xl">
                <h4 className="font-medium text-secondary-700 mb-3 flex items-center gap-2">
                  <Waves className="w-5 h-5" />
                  Baptism Request
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {selectedSubmission.salvationDate && (
                    <div>
                      <label className="text-sm text-gray-500">Date Accepted Christ</label>
                      <p className="font-medium text-dark-100">{selectedSubmission.salvationDate}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Volunteer Fields */}
            {selectedSubmission.formType === 'volunteer' && (
              <div className="bg-accent-50 p-4 rounded-xl">
                <h4 className="font-medium text-accent-700 mb-3 flex items-center gap-2">
                  <HeartHandshake className="w-5 h-5" />
                  Volunteer Interest
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {selectedSubmission.ministry && (
                    <div>
                      <label className="text-sm text-gray-500">Ministry Area</label>
                      <p className="font-medium text-dark-100 capitalize">{selectedSubmission.ministry}</p>
                    </div>
                  )}
                  {selectedSubmission.availability && (
                    <div>
                      <label className="text-sm text-gray-500">Availability</label>
                      <p className="font-medium text-dark-100 capitalize">{selectedSubmission.availability}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Contact Form Fields */}
            {selectedSubmission.formType === 'contact' && selectedSubmission.subject && (
              <div>
                <label className="text-sm text-gray-500">Subject</label>
                <p className="font-medium text-dark-100">{selectedSubmission.subject}</p>
              </div>
            )}

            {/* Message/Notes */}
            {selectedSubmission.message && (
              <div>
                <label className="text-sm text-gray-500">Message</label>
                <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                  {selectedSubmission.message}
                </p>
              </div>
            )}

            {/* Prayer Request */}
            {selectedSubmission.prayerRequest && (
              <div>
                <label className="text-sm text-gray-500">Prayer Request</label>
                <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                  {selectedSubmission.prayerRequest}
                </p>
              </div>
            )}

            {/* Connect Card Fields */}
            {selectedSubmission.formType === 'connect-card' && (
              <div className="bg-amber-50 p-4 rounded-xl">
                <h4 className="font-medium text-amber-700 mb-3 flex items-center gap-2">
                  <HandHeart className="w-5 h-5" />
                  Connect Card
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-500">Visitor Type</label>
                    <p className="font-medium text-dark-100">
                      {selectedSubmission.visitorType === 'first_time' ? 'First-time visitor' : 'Returning visitor'}
                    </p>
                  </div>
                  {selectedSubmission.serviceAttended && (
                    <div>
                      <label className="text-sm text-gray-500">Service Attended</label>
                      <p className="font-medium text-dark-100 capitalize">
                        {selectedSubmission.serviceAttended.replace('_', ' ')}
                      </p>
                    </div>
                  )}
                  {selectedSubmission.interests?.length > 0 && (
                    <div className="md:col-span-2">
                      <label className="text-sm text-gray-500">Interests</label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {selectedSubmission.interests.map((i) => (
                          <span key={i} className="px-2 py-1 rounded-full bg-white text-amber-700 text-xs font-medium border border-amber-200 capitalize">
                            {i.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Visit Fields */}
            {selectedSubmission.formType === 'visit' && (
              <div className="bg-green-50 p-4 rounded-xl">
                <h4 className="font-medium text-green-700 mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Visit Registration
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {selectedSubmission.visitDate && (
                    <div>
                      <label className="text-sm text-gray-500">Visit Date</label>
                      <p className="font-medium text-dark-100">{formatDate(selectedSubmission.visitDate)}</p>
                    </div>
                  )}
                  {selectedSubmission.serviceTime && (
                    <div>
                      <label className="text-sm text-gray-500">Service Time</label>
                      <p className="font-medium text-dark-100">{selectedSubmission.serviceTime}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4 border-t">
              <Button
                variant="danger"
                icon={Trash2}
                onClick={() => setDeleteConfirm(selectedSubmission)}
              >
                {isNewsletterTab ? 'Unsubscribe' : 'Delete'}
              </Button>
              <Button
                variant="primary"
                icon={Mail}
                onClick={() => window.location.href = `mailto:${selectedSubmission.email}`}
              >
                {isNewsletterTab ? 'Send Email' : 'Reply via Email'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title={isNewsletterTab ? "Remove Subscriber" : "Delete Submission"}
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          {isNewsletterTab
            ? `Are you sure you want to remove ${deleteConfirm?.email} from the newsletter list? This action cannot be undone.`
            : 'Are you sure you want to delete this submission? This action cannot be undone.'
          }
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            {isNewsletterTab ? 'Remove' : 'Delete'}
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default FormSubmissions
